/**
 * UserActivityTimeline - Timeline des actions d'un utilisateur
 *
 * Affiche chronologiquement toutes les actions d'un utilisateur:
 * - Consultations (view)
 * - Creations, modifications, suppressions
 * - Actions workflow (submit, approve, reject...)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../../utils/apiConfig';
import ActivityIcon from './ActivityIcon';
import ActivityDiff from './ActivityDiff';

const UserActivityTimeline = ({
  userId,
  token,
  startDate = null,
  endDate = null,
  limit = 50,
  showHeader = true,
  onActivityClick = null
}) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTimeline = useCallback(async () => {
    if (!userId || !token) return;

    setLoading(true);
    setError(null);

    try {
      let url = `${API_ENDPOINTS.BASE_URL}/api/analytics/dashboard/user-timeline/${userId}?limit=${limit}`;
      if (startDate) url += `&startDate=${startDate}`;
      if (endDate) url += `&endDate=${endDate}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Acces non autorise');
        }
        throw new Error('Erreur lors du chargement');
      }

      const result = await response.json();

      if (result.success) {
        setActivities(result.data.activities || []);
      } else {
        throw new Error(result.error || 'Erreur inconnue');
      }
    } catch (err) {
      console.error('Erreur chargement timeline:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, token, startDate, endDate, limit]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'A l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;

    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const groupByDate = (activities) => {
    const groups = {};

    activities.forEach(activity => {
      const date = new Date(activity.timestamp).toLocaleDateString('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });

      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(activity);
    });

    return groups;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
        <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
        Chargement de l'historique...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        background: '#ffebee',
        borderRadius: '8px',
        color: '#c62828',
        textAlign: 'center'
      }}>
        {error}
      </div>
    );
  }

  const groupedActivities = groupByDate(activities);

  return (
    <div style={{
      background: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
    }}>
      {showHeader && (
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #eee',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: '#f8f9fa'
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
            📊 Historique d'activite
          </h3>
          <span style={{ fontSize: '13px', color: '#666' }}>
            {activities.length} evenement{activities.length > 1 ? 's' : ''}
          </span>
        </div>
      )}

      <div style={{ padding: '16px 20px' }}>
        {activities.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
            Aucune activite enregistree
          </div>
        ) : (
          Object.entries(groupedActivities).map(([date, dayActivities]) => (
            <div key={date} style={{ marginBottom: '24px' }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#666',
                marginBottom: '12px',
                textTransform: 'capitalize'
              }}>
                {date}
              </div>

              <div style={{
                borderLeft: '2px solid #e0e0e0',
                marginLeft: '18px',
                paddingLeft: '20px'
              }}>
                {dayActivities.map((activity, idx) => (
                  <div
                    key={activity.id || idx}
                    onClick={() => onActivityClick && onActivityClick(activity)}
                    style={{
                      position: 'relative',
                      marginBottom: '16px',
                      padding: '12px',
                      background: '#fafafa',
                      borderRadius: '8px',
                      cursor: onActivityClick ? 'pointer' : 'default',
                      transition: 'background 0.2s',
                      ':hover': { background: '#f0f0f0' }
                    }}
                  >
                    {/* Point sur la timeline */}
                    <div style={{
                      position: 'absolute',
                      left: '-29px',
                      top: '16px',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      background: 'white',
                      border: '3px solid #1976d2'
                    }} />

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <ActivityIcon type={activity.type} size="medium" />

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          marginBottom: '4px',
                          color: '#333'
                        }}>
                          {activity.label}
                        </div>

                        {activity.entityLabel && (
                          <div style={{
                            fontSize: '13px',
                            color: '#1976d2',
                            marginBottom: '4px'
                          }}>
                            {activity.entityLabel}
                          </div>
                        )}

                        <div style={{
                          fontSize: '12px',
                          color: '#999'
                        }}>
                          {formatTimestamp(activity.timestamp)}
                          {activity.module && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 6px',
                              background: '#e3f2fd',
                              borderRadius: '4px',
                              fontSize: '10px',
                              textTransform: 'uppercase'
                            }}>
                              {activity.module}
                            </span>
                          )}
                        </div>

                        {activity.details && (
                          <ActivityDiff details={activity.details} collapsed={true} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UserActivityTimeline;
